import React, { useState, useEffect } from "react";
import Header from "./Header";
import AppCard from "./AppCard";
import "./Dashboard.css";

function Dashboard({
  apps = [],
  categories = [],
  searchQuery = "",
  onSearchChange,
  viewMode = "grid",
  onToggleViewMode,
  onToggleFavorite,
  onDeleteApp,
  userName = "Daniel Radcliffe"
}) {
  // Track expanded/collapsed categories (key: categoryName, value: boolean)
  const [expandedSections, setExpandedSections] = useState({});

  // Initialize all sections as expanded by default
  useEffect(() => {
    const initialExpanded = {};
    categories.forEach((cat) => {
      initialExpanded[cat] = true;
    });
    setExpandedSections(initialExpanded);
  }, [categories]);

  const toggleSection = (cat) => {
    setExpandedSections((prev) => ({
      ...prev,
      [cat]: !prev[cat]
    }));
  };

  // Get dynamic greeting based on system time
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = userName.split(" ")[0];
    if (hour < 12) return `Good Morning ${firstName}`;
    if (hour < 17) return `Good Afternoon ${firstName}`;
    return `Good Evening ${firstName}`;
  };

  // Filter apps by search query
  const getFilteredApps = (category) => {
    let list = apps.filter((app) => {
      // Favorite category is dynamic and includes any app marked as favorite
      if (category.toLowerCase() === "favorites") {
        return app.isFavorite;
      }
      return app.category.toLowerCase() === category.toLowerCase();
    });

    if (searchQuery.trim()) {
      list = list.filter((app) =>
        app.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return list;
  };

  return (
    <div className="dashboard-content">
      {/* Dynamic Header */}
      <Header searchQuery={searchQuery} onSearchChange={onSearchChange} />

      {/* Main Greeting Bar */}
      <div className="dashboard__welcome-bar">
        <h1 className="dashboard__greeting-text">{getGreeting()}</h1>

        {/* View Mode Switcher */}
        <div className="dashboard__view-switcher">
          <button
            className={`dashboard__view-btn ${viewMode === "list" ? "active" : ""}`}
            onClick={() => onToggleViewMode("list")}
            aria-label="List view"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="8" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="8" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="8" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx="4" cy="6" r="1.5" fill="currentColor"/>
              <circle cx="4" cy="12" r="1.5" fill="currentColor"/>
              <circle cx="4" cy="18" r="1.5" fill="currentColor"/>
            </svg>
          </button>
          <button
            className={`dashboard__view-btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => onToggleViewMode("grid")}
            aria-label="Grid view"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2.5"/>
              <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2.5"/>
              <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2.5"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2.5"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Accordion List Categories */}
      <div className="dashboard__sections-list">
        {categories.map((cat) => {
          const categoryApps = getFilteredApps(cat);
          const isExpanded = expandedSections[cat] ?? true;

          // If search is active and category has no matching apps, hide the whole section to look clean
          if (searchQuery.trim() && categoryApps.length === 0) return null;

          return (
            <div key={cat} className="dashboard__section-accordion">
              {/* Accordion Header */}
              <button
                className={`dashboard__section-header ${isExpanded ? "expanded" : ""}`}
                onClick={() => toggleSection(cat)}
              >
                <div className="dashboard__section-header-left">
                  <span className="dashboard__section-title">{cat}</span>
                  <span className="dashboard__section-badge">
                    {categoryApps.length} App{categoryApps.length !== 1 ? "s" : ""} included
                  </span>
                </div>
                <svg
                  className={`dashboard__section-arrow ${isExpanded ? "rotated" : ""}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Accordion Body */}
              {isExpanded && (
                <div className={`dashboard__section-body dashboard__section-body--${viewMode}`}>
                  {categoryApps.length === 0 ? (
                    <div className="dashboard__empty-state glass-card">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <p>No apps in this section yet.</p>
                    </div>
                  ) : (
                    categoryApps.map((app) => (
                      <AppCard
                        key={app.id}
                        app={app}
                        onToggleFavorite={onToggleFavorite}
                        onDeleteApp={onDeleteApp}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* If search resulted in absolutely zero apps across all categories */}
        {searchQuery.trim() &&
          categories.every((cat) => getFilteredApps(cat).length === 0) && (
            <div className="dashboard__global-empty glass-card">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="var(--text-muted)" strokeWidth="2"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <h2>No Matching Apps Found</h2>
              <p>Try searching for a different keyword or create a new app card in the sidebar.</p>
            </div>
          )}
      </div>
    </div>
  );
}

export default Dashboard;
